class MarketController {
  constructor(apiService) {
    this.apiService = apiService;
  }

  /**
   * Create a new market listing
   * @param {Object} listingData - The listing data
   * @returns {Promise<Object>} - The created listing
   */
  async createListing(listingData) {
    try {
      const response = await this.apiService.post('/market/listings', listingData);
      return response.data;
    } catch (error) {
      console.error('Error creating listing:', error);
      throw error;
    }
  }

  /**
   * Update an existing listing
   * @param {string} listingId - The listing ID
   * @param {Object} listingData - The updated listing data
   * @returns {Promise<Object>} - The updated listing
   */
  async updateListing(listingId, listingData) {
    try {
      const response = await this.apiService.put(`/market/listings/${listingId}`, listingData);
      return response.data;
    } catch (error) {
      console.error('Error updating listing:', error);
      throw error;
    }
  }

  /**
   * Delete a listing (archives it instead of hard delete)
   * @param {string} listingId - The listing ID
   * @returns {Promise<void>}
   */
  async deleteListing(listingId) {
    try {
      await this.apiService.delete(`/market/listings/${listingId}`);
    } catch (error) {
      console.error('Error archiving listing:', error);
      throw error;
    }
  }

  /**
   * Mark a listing as sold (archives it with sold status)
   * @param {string} listingId - The listing ID
   * @returns {Promise<Object>} - The updated listing
   */
  async markAsSold(listingId) {
    try {
      const response = await this.apiService.put(`/market/listings/${listingId}/mark-sold`);
      return response.data;
    } catch (error) {
      console.error('Error marking as sold:', error);
      throw error;
    }
  }

  /**
   * Remove a listing
   * @param {string} listingId - The listing ID
   * @returns {Promise<Object>} - The updated listing
   */
  async relistListing(listingId) {
    try {
      const response = await this.apiService.put(`/market/listings/${listingId}/relist`);
      return response.data;
    } catch (error) {
      console.error('Error relisting listing:', error);
      throw error;
    }
  }

  async removeListing(listingId) {
    try {
      const response = await this.apiService.put(`/market/listings/${listingId}/remove`);
      return response.data;
    } catch (error) {
      console.error('Error removing listing:', error);
      throw error;
    }
  }

  /**
   * Get a listing by ID
   * @param {string} listingId - The listing ID
   * @returns {Promise<Object>} - The listing details
   */
  async getListingById(listingId) {
    try {
      const response = await this.apiService.get(`/market/listings/${listingId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching listing:', error);
      throw error;
    }
  }

  /**
   * Search listings with filters
   * @param {Object} filters - Search filters
   * @returns {Promise<Object>} - Search results with pagination
   */
  async searchListings(filters = {}) {
    try {
      const params = {};
      
      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
          if (key === 'page' || key === 'limit') {
            params[key] = Number(filters[key]);
          } else if (key === 'minPrice' || key === 'maxPrice') {
            params[key] = Number(filters[key]);
          } else {
            params[key] = filters[key];
          }
        }
      });

      const response = await this.apiService.get('/market/listings/search', { params });
      return response.data;
    } catch (error) {
      console.error('Error searching listings:', error);
      throw error;
    }
  }

  /**
   * Get current user's listings
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @returns {Promise<Object>} - User's listings
   */
  async getMyListings(page = 1, limit = 20) {
    try {
      const response = await this.apiService.get(`/market/listings/my-listings`, {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching my listings:', error);
      throw error;
    }
  }

  /**
   * Get current user's archived listings (sold/deleted)
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @returns {Promise<Object>} - User's archived listings
   */
  async getArchivedListings(page = 1, limit = 20) {
    try {
      const response = await this.apiService.get(`/market/listings/my-activity`, {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching archived listings:', error);
      throw error;
    }
  }

  /**
   * Get saved listings
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @returns {Promise<Object>} - Saved listings
   */
  async getSavedListings(page = 1, limit = 20) {
    try {
      const response = await this.apiService.get(`/market/listings/saved`, {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching saved listings:', error);
      throw error;
    }
  }

  /**
   * Save a listing
   * @param {string} listingId - The listing ID
   * @returns {Promise<Object>} - Success message
   */
  async saveListing(listingId) {
    try {
      const response = await this.apiService.post(`/market/listings/${listingId}/save`);
      return response.data;
    } catch (error) {
      console.error('Error saving listing:', error);
      throw error;
    }
  }

  /**
   * Unsave a listing
   * @param {string} listingId - The listing ID
   * @returns {Promise<Object>} - Success message
   */
  async unsaveListing(listingId) {
    try {
      const response = await this.apiService.delete(`/market/listings/${listingId}/save`);
      return response.data;
    } catch (error) {
      console.error('Error unsaving listing:', error);
      throw error;
    }
  }

  /**
   * Check if a listing is saved
   * @param {string} listingId - The listing ID
   * @returns {Promise<boolean>} - Whether the listing is saved
   */
  async isListingSaved(listingId) {
    try {
      const response = await this.apiService.get(`/market/listings/${listingId}/is-saved`);
      return response.data.isSaved;
    } catch (error) {
      console.error('Error checking if listing is saved:', error);
      throw error;
    }
  }

  /**
   * Get listings by user ID
   * @param {string} userId - The user ID
   * @returns {Promise<Object>} - User's listings
   */
  async getUserListings(userId) {
    try {
      const response = await this.apiService.get(`/market/listings/user/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user listings:', error);
      throw error;
    }
  }

  /**
   * Search figures for autocomplete
   * @param {Object|string} queryOrParams - Search query string or params object with {query, limit}
   * @param {number} limit - Max results (only used if first param is string)
   * @returns {Promise<Array>} - Matching figures
   */
  async searchFigures(queryOrParams, limit = 10) {
    try {
      let query, limitValue;
      
      if (typeof queryOrParams === 'object') {
        query = queryOrParams.query;
        limitValue = queryOrParams.limit || 10;
      } else {
        query = queryOrParams;
        limitValue = limit;
      }
      
      const parsedLimit = parseInt(limitValue, 10);
      const finalLimit = isNaN(parsedLimit) ? 10 : Math.min(Math.max(parsedLimit, 1), 20);
      
      const response = await this.apiService.get('/market/figures/search', {
        params: {
          query: String(query),
          limit: finalLimit
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching figures:', error);
      throw error;
    }
  }
}

export default MarketController;
